import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { AutocompleteCDS, CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LoanEditorActionsEnum } from 'constants/loanActionEditor';

import { selectMortgageInsuranceProviderOptions } from 'store/system/selectors';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';
import { wrapperClassname } from '../LoanActionItem.constants';
import { getOptionLabel, isOptionEqual } from '../LoanActionSelectOption.utils';

import { miApplicationTypeSelectOptions } from './LoanActionMortgageInsuranceRow.constants';
import {
  getMiProviderOptions,
  getSelectedMiApplicationTypeOption,
  getSelectedMiProviderOption,
} from './LoanActionMortgageInsuranceRow.utils';

const LoanActionMortgageInsuranceRow = ({ itemType, rootPath, isDisabled, isEditAllowed }) => {
  const { form, setMultipleInForm } = useFormContext();
  const providerOptions = useSelector(selectMortgageInsuranceProviderOptions);

  const mortgageInsurance = _.get(form, rootPath) ?? {};
  const { providerKey, applicationType, resubmission } = mortgageInsurance;
  const isOrderAction = itemType === LoanEditorActionsEnum.ORDER_MORTGAGE_INSURANCE;
  const isFieldDisabled = isDisabled || !isEditAllowed;

  const miProviderOptions = useMemo(
    () => getMiProviderOptions({ providerOptions, providerKey }),
    [providerOptions, providerKey],
  );

  const selectedProvider = getSelectedMiProviderOption({
    options: miProviderOptions,
    providerKey,
  });
  const selectedApplicationType = getSelectedMiApplicationTypeOption(applicationType);

  const onProviderChange = useCallback(
    (__, option) => {
      setMultipleInForm({ [`${rootPath}.providerKey`]: option?.value ?? null });
    },
    [rootPath, setMultipleInForm],
  );

  const onApplicationTypeChange = useCallback(
    (__, option) => {
      setMultipleInForm({
        [`${rootPath}.applicationType`]: _.isNil(option?.value) ? null : option,
      });
    },
    [rootPath, setMultipleInForm],
  );

  const onResubmissionChange = useCallback(
    event => {
      setMultipleInForm({ [`${rootPath}.resubmission`]: event.target.checked });
    },
    [rootPath, setMultipleInForm],
  );

  return (
    <Box className={wrapperClassname}>
      <AutocompleteCDS
        disableClearable
        dataTestId={loanManagementTestIds.getMortgageInsuranceProviderSelectId(rootPath)}
        disabled={isFieldDisabled}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqual}
        label="MI Provider"
        name={`${rootPath}.providerKey`}
        options={miProviderOptions}
        value={selectedProvider}
        onChange={onProviderChange}
      />
      <AutocompleteCDS
        disableClearable
        dataTestId={loanManagementTestIds.getMortgageInsuranceApplicationTypeSelectId(rootPath)}
        disabled={isFieldDisabled}
        getOptionLabel={getOptionLabel}
        isHidden={!isOrderAction}
        isOptionEqualToValue={isOptionEqual}
        label="Application Type"
        name={`${rootPath}.applicationType`}
        options={miApplicationTypeSelectOptions}
        value={selectedApplicationType}
        onChange={onApplicationTypeChange}
      />
      <CheckboxCDS
        dataTestId={loanManagementTestIds.getMortgageInsuranceResubmissionCheckboxId(rootPath)}
        disabled={isFieldDisabled}
        isHidden={!isOrderAction}
        label="Resubmit to insurer (re-underwrite a suspended or conditioned order)"
        name={`${rootPath}.resubmission`}
        value={!!resubmission}
        onChange={onResubmissionChange}
      />
    </Box>
  );
};

LoanActionMortgageInsuranceRow.propTypes = {
  itemType: PropTypes.string.isRequired,
  rootPath: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
};

export default memo(LoanActionMortgageInsuranceRow);
